import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { staticImages } from "../../../modules/configs/appConfigs";
import { ResponsivePresetKeys, ResponsivePresets } from "../../../modules/configs/responsiveImageConfigs";
import { EpisodeReleaseStatus } from "../../../modules/graphql/api.generated";
import { getReleaseTime } from "../../../modules/helpers/programService";
import { isExternalLink } from "../../../modules/helpers/urlHelpers";
import { FocusableComponentLayout, useFocusable } from "../../../modules/spatialNavigation";
import ImageGenerator from "../imageGenerator/ImageGenerator";
import * as styles from "./styles.module.scss";

interface IProps {
  imagePath: string;
  link?: string;
  title?: string;
  focusKey?: string;
  index?: number;
  resumeInfo?: {
    seconds: number;
    duration: number;
  };
  isFree?: boolean;
  isExclusive?: boolean;
  releaseStatus?: EpisodeReleaseStatus;
  releaseDate?: string;
  checkSubscription?: boolean;
  countPerRow?: number;
  onClick?: () => void;
  onMovieFocus?: () => void;
  onKeydown?: (e: any) => void;
  onEnterPress?: () => void;
  onFocus: (layout: FocusableComponentLayout) => void;
}

const MovieCard = (props: IProps) => {
  const { ref, focused } = useFocusable({
    onEnterPress: () => handleClick(),
    onFocus: (l) => {
      return props.onFocus(l);
    },
    focusKey: props.focusKey,
  });
  const history = useHistory();
  const [countdown, setCountdown] = useState<{ days?: number; hours?: number; minutes?: number }>({});
  const progressValue = Math.round(
    !!props.resumeInfo ? (props.resumeInfo.seconds / props.resumeInfo.duration) * 100 : 0,
  );

  useEffect(() => {
    if (props.releaseDate) {
      setCountdown(getReleaseTime(props.releaseDate));
      const intervalId = setInterval(() => setCountdown(getReleaseTime(props.releaseDate)), 6000); // Update every minute
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [props.releaseDate]);

  const handleClick = () => {
    if (props.onClick || props.onEnterPress) {
      if (props.onClick) {
        props.onClick();
      }
      if (props.onEnterPress) {
        props.onEnterPress();
      }
    } else if (!props.releaseStatus || props.releaseStatus === EpisodeReleaseStatus.Released) {
      if (isExternalLink(props.link)) {
        window.location.href = props.link;
      } else {
        history.push(props.link);
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      ref={ref}
      style={{ width: `calc(100% / ${props.countPerRow ?? 5})`, minWidth: `calc(100% / ${props.countPerRow ?? 5})` }}
      className={`${styles.movieCard} ${focused ? styles.focused : ""}`}
    >
      {props.isExclusive && (
        <img className={styles.exclusiveBadge} src={staticImages.miniLogoWhite} alt="اختصاصی شیدا" />
      )}
      {props.releaseStatus && props.releaseStatus === EpisodeReleaseStatus.Upcoming && (
        <div className={styles.comingSoonLabel}>
          تا {props.title} :{countdown && countdown.days !== undefined && <span> {countdown.days} روز</span>}
          {countdown && countdown.hours !== undefined && <span> {countdown.hours} ساعت</span>}
          {countdown && countdown.minutes !== undefined && <span> {countdown.minutes} دقیقه</span>}
        </div>
      )}
      {props.releaseStatus && props.releaseStatus === EpisodeReleaseStatus.ComingSoon && (
        <div className={`${styles.comingSoonLabel} ${styles.upcomingLabel}`}>{props.title} : به زودی</div>
      )}
      <ImageGenerator
        lazyLoad
        noBlurPlaceholder={true}
        imageType="landscape"
        sizes={ResponsivePresets[ResponsivePresetKeys.EXPANDABLE_CARD_CAROUSEL]}
        src={props.imagePath}
      />

      {props.isFree && <div className={styles.typeBadge}>رایگان</div>}

      {props.title?.length! > 0 && (!props.releaseStatus || props.releaseStatus !== EpisodeReleaseStatus.Upcoming) && (
        <div className={styles.titleBox}>
          <span>{props.title}</span>
        </div>
      )}

      {!!props.resumeInfo && (
        <div style={{ transition: "all .2s", marginTop: focused ? "16px" : "8px" }}>
          <div className={styles.linearProgress}>
            <div className={styles.progressBar} style={{ width: `${progressValue}%` }} />
          </div>
        </div>
      )}
    </div>
  );
};
export default MovieCard;
